import { useCallback, useState } from 'react';
import type { TFunction } from 'i18next';
import { EmployeeService } from 'services/EmployeeService';
import type { PositionRow } from './useAssignPositionForm';

const employeeService = new EmployeeService();

interface HandlerAlertArgs {
    title: string;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

interface Props {
    employee_id: number;
    onSuccess: () => void;
    reloading: () => void;
    handlerAlert: (open: boolean, args: HandlerAlertArgs) => void;
    t: TFunction;
}

// Encapsula la acción de guardado de las asignaciones de puesto contra el microservicio.
export const useAssignPositionActions = ({ employee_id, onSuccess, reloading, handlerAlert, t }: Props) => {
    const [saving, setSaving] = useState(false);

    const saveAssignments = useCallback(
        async (hiringId: number, rows: PositionRow[]) => {
            const orgpath = rows.map((row) => ({
                hiring_orpath_id: row.hiring_orgpath_id ?? null,
                to_delete: row.to_delete,
                subcompany: row.subcompany_id,
                areas: row.area_ids,
                puesto: row.work_position_id,
                start_date: row.start_date?.format('YYYY-MM-DD') ?? '',
                end_date: row.end_date?.format('YYYY-MM-DD') ?? '',
            }));

            const body = {
                employee_id,
                hiring_id: hiringId,
                orgpath,
            };

            console.log('Assigning positions with body:', body);
            try {
                setSaving(true);
                await employeeService.employee.assignHiringOrgpath(body);
                handlerAlert(true, {
                    title: t('timekeeper.employees.dialog.assign_position.messages.success', 'Éxito'),
                    message: t('timekeeper.employees.dialog.assign_position.messages.success', 'La posición se ha asignado correctamente'),
                    severity: 'success',
                });
                onSuccess();
                reloading();
            } catch (error) {
                console.error('Error assigning positions', error);
                handlerAlert(true, {
                    title: t('timekeeper.employees.dialog.assign_position.messages.error', 'Error'),
                    message: t('timekeeper.employees.dialog.assign_position.messages.error', 'Ha ocurrido un error al asignar la posición'),
                    severity: 'error',
                });
            } finally {
                setSaving(false);
            }
        },
        [employee_id, onSuccess, reloading, handlerAlert, t],
    );

    return { saving, saveAssignments };
};
